import { useCallback, useEffect, useMemo, useState } from "react";
import { CRMLayout } from "@/components/CRMLayout";
import { useAuth } from "@/App";
import { toast } from "sonner";
import {
  Bot, CheckCircle2, CirclePause, Copy, GitBranch, GitCommit, Github, KeyRound,
  Laptop, Loader2, RefreshCw, Server, Trash2, X,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SOURCES = {
  orca: { label: "Orca (Mac)", icon: Laptop, cls: "bg-violet-50 text-violet-700 border-violet-200" },
  github: { label: "GitHub / web", icon: Github, cls: "bg-slate-100 text-slate-700 border-slate-200" },
  mcp: { label: "Connecteur MCP", icon: Bot, cls: "bg-sky-50 text-sky-700 border-sky-200" },
  novaforge: { label: "Agent NovaForge", icon: Server, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  codex: { label: "Codex", icon: Bot, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  autre: { label: "Autre", icon: Bot, cls: "bg-slate-50 text-slate-600 border-slate-200" },
};
export const STATUTS = {
  active: { label: "Active", cls: "bg-emerald-100 text-emerald-700" },
  en_pause: { label: "En pause", cls: "bg-amber-100 text-amber-700" },
  terminee: { label: "Terminée", cls: "bg-slate-100 text-slate-600" },
  abandonnee: { label: "Abandonnée", cls: "bg-red-100 text-red-700" },
};
const EVENEMENTS = { debut: "▶️", note: "📝", decision: "✅", commit: "🔀", tache: "📌", fin: "🏁", battement: "·" };

export function depuis(iso) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  return `il y a ${Math.floor(s / 86400)} j`;
}

export function Detail({ id, token, onClose }) {
  const [session, setSession] = useState(null);
  useEffect(() => {
    fetch(`${API_URL}/api/agent/sync/sessions/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then(setSession).catch(() => toast.error("Session introuvable"));
  }, [id, token]);
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/30" onClick={onClose}>
      <aside className="h-full w-full max-w-xl overflow-y-auto bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-bold text-slate-900">{session?.title || "Session"}</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>
        {!session ? <Loader2 className="mt-6 h-5 w-5 animate-spin text-slate-400" /> : (
          <>
            <p className="mt-1 text-sm text-slate-500">{session.project_name || "Sans projet"} · {session.machine || SOURCES[session.source]?.label}</p>
            {session.branch && <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 font-mono text-xs"><GitBranch className="h-3.5 w-3.5" />{session.branch}</p>}
            {session.summary && <div className="mt-4 rounded-xl border border-violet-100 bg-violet-50/60 p-3 text-sm text-slate-800 whitespace-pre-wrap">{session.summary}</div>}
            {session.last_commits?.length > 0 && (
              <div className="mt-4">
                <h3 className="text-xs font-bold uppercase text-slate-500">Derniers commits</h3>
                <ul className="mt-2 space-y-1">{session.last_commits.map((c) => (
                  <li key={c} className="flex items-start gap-1.5 font-mono text-xs text-slate-700"><GitCommit className="mt-0.5 h-3.5 w-3.5 shrink-0" />{c}</li>))}</ul>
              </div>
            )}
            <h3 className="mt-6 text-xs font-bold uppercase text-slate-500">Journal</h3>
            <ol className="mt-2 space-y-3 border-l-2 border-slate-100 pl-4">
              {(session.events || []).filter((e) => e.type !== "battement").map((e) => (
                <li key={e.id} className="text-sm">
                  <span className="mr-1">{EVENEMENTS[e.type] || "•"}</span>
                  <span className="text-xs text-slate-400">{new Date(e.created_at).toLocaleString("fr-FR")}</span>
                  <p className="mt-0.5 whitespace-pre-wrap text-slate-800">{e.text}</p>
                </li>))}
            </ol>
          </>
        )}
      </aside>
    </div>
  );
}

function Appareils({ token }) {
  const [liste, setListe] = useState([]);
  const [nom, setNom] = useState("");
  const [nouveau, setNouveau] = useState(null);
  const charger = useCallback(() => {
    fetch(`${API_URL}/api/agent/sync/devices`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : [])).then(setListe);
  }, [token]);
  useEffect(() => { charger(); }, [charger]);
  const creer = async () => {
    const r = await fetch(`${API_URL}/api/agent/sync/devices`, {
      method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ name: nom || "Mac Orca" }),
    });
    if (!r.ok) return toast.error("Création impossible");
    setNouveau(await r.json()); setNom(""); charger();
  };
  const revoquer = async (id) => {
    await fetch(`${API_URL}/api/agent/sync/devices/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    charger();
  };
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5">
      <h2 className="flex items-center gap-2 font-bold text-slate-900"><KeyRound className="h-4 w-4" />Appareils synchronisés</h2>
      <p className="mt-1 text-sm text-slate-500">Chaque Mac qui fait tourner Orca envoie ses sessions avec un jeton dédié.</p>
      <div className="mt-3 flex gap-2">
        <input value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Nom de l'appareil (ex. iMac bureau)" className="flex-1 rounded-lg border px-3 py-2 text-sm" />
        <button onClick={creer} className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white">Créer un jeton</button>
      </div>
      {nouveau && (
        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm">
          <p className="font-semibold text-amber-800">Jeton de « {nouveau.name} » — affiché une seule fois :</p>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 break-all rounded bg-white px-2 py-1 text-xs">{nouveau.token}</code>
            <button onClick={() => { navigator.clipboard.writeText(nouveau.token); toast.success("Copié"); }} className="rounded-lg border bg-white p-2"><Copy className="h-4 w-4" /></button>
          </div>
        </div>
      )}
      <ul className="mt-3 divide-y text-sm">
        {liste.map((a) => (
          <li key={a.id} className="flex items-center justify-between py-2">
            <span className={a.revoked ? "text-slate-400 line-through" : "text-slate-800"}>{a.name}</span>
            <span className="text-xs text-slate-400">{a.last_seen ? `vu ${depuis(a.last_seen)}` : "jamais connecté"}</span>
            {!a.revoked && <button onClick={() => revoquer(a.id)} title="Révoquer" className="rounded p-1 text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
          </li>))}
      </ul>
    </section>
  );
}

export default function SessionsPage() {
  const { token, user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [source, setSource] = useState("");
  const [ouverte, setOuverte] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const r = await fetch(`${API_URL}/api/agent/sync/sessions?jours=30${source ? `&source=${source}` : ""}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setSessions(r.ok ? await r.json() : []);
    } finally { setChargement(false); }
  }, [token, source]);
  useEffect(() => { charger(); const t = setInterval(charger, 60000); return () => clearInterval(t); }, [charger]);

  const parProjet = useMemo(() => {
    const groupes = {};
    sessions.forEach((s) => { (groupes[s.project_name || "Sans projet"] ||= []).push(s); });
    return Object.entries(groupes);
  }, [sessions]);
  const actives = sessions.filter((s) => s.status === "active").length;

  return (
    <CRMLayout>
      <div className="mx-auto max-w-6xl space-y-6 p-4 md:p-8">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Sessions IA</h1>
            <p className="text-sm text-slate-500">Toutes les sessions de travail — Orca, GitHub, connecteur MCP et agents NovaForge — rattachées à leurs projets. {actives} active{actives > 1 ? "s" : ""}.</p>
          </div>
          <div className="flex gap-2">
            <select value={source} onChange={(e) => setSource(e.target.value)} className="rounded-lg border px-3 py-2 text-sm">
              <option value="">Toutes les sources</option>
              {Object.entries(SOURCES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <button onClick={charger} className="rounded-lg border px-3 py-2"><RefreshCw className={`h-4 w-4 ${chargement ? "animate-spin" : ""}`} /></button>
          </div>
        </header>

        {!chargement && sessions.length === 0 && (
          <div className="rounded-2xl border border-dashed p-10 text-center text-sm text-slate-500">Aucune session sur les 30 derniers jours.</div>
        )}

        {parProjet.map(([projet, lot]) => (
          <section key={projet}>
            <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-slate-500">{projet}</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {lot.map((s) => {
                const src = SOURCES[s.source] || SOURCES.autre;
                const Icone = src.icon;
                const st = STATUTS[s.status] || STATUTS.active;
                return (
                  <button key={s.id} onClick={() => setOuverte(s.id)} className="rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm hover:border-violet-300">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${src.cls}`}><Icone className="h-3.5 w-3.5" />{src.label}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${st.cls}`}>{s.status === "active" ? <CheckCircle2 className="mr-1 inline h-3 w-3" /> : s.status === "en_pause" ? <CirclePause className="mr-1 inline h-3 w-3" /> : null}{st.label}</span>
                      <span className="ml-auto text-xs text-slate-400">{depuis(s.last_seen || s.updated_at)}</span>
                    </div>
                    <p className="mt-2 line-clamp-2 font-semibold text-slate-900">{s.title || "Session sans titre"}</p>
                    {s.summary && <p className="mt-1 line-clamp-2 text-sm text-slate-500">{s.summary}</p>}
                    <p className="mt-2 flex flex-wrap gap-3 text-xs text-slate-400">
                      {s.branch && <span className="inline-flex items-center gap-1 font-mono"><GitBranch className="h-3.5 w-3.5" />{s.branch}</span>}
                      {s.machine && <span>{s.machine}</span>}
                      {s.last_commits?.length > 0 && <span className="inline-flex items-center gap-1"><GitCommit className="h-3.5 w-3.5" />{s.last_commits.length} commit{s.last_commits.length > 1 ? "s" : ""}</span>}
                    </p>
                  </button>);
              })}
            </div>
          </section>
        ))}

        {user?.role === "admin" && <Appareils token={token} />}
      </div>
      {ouverte && <Detail id={ouverte} token={token} onClose={() => setOuverte(null)} />}
    </CRMLayout>
  );
}
